import {
  Col,
  Row,
  Slider,
  Flex,
  Typography,
  Image,
  Card,
  Tabs,
  InputNumber,
  Progress,
  Alert,
} from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import ViewOnlyDocumentBlock from "../../../components/ViewOnlyDocument/ViewOnlyDocumentBlock";
import ReturnsImage from "../../../assets/images/returns.png";
import MaturityImage from "../../../assets/icons/maturity.svg";
import DealValueImage from "../../../assets/images/dealValue.png";
import CustomButton from "../../../components/CustomButton/Button";
import KarncyEvaluation from "./KarncyEvaluation";
import Enterprise from "./Enterprise";
import Business from "./Business";
import { getDealById, getDealSections } from "../../../redux/slices/DealSlice";
import { useSelector, useDispatch } from "react-redux";
import Loader from "../../../components/CustomLoader/Loader";
import SoldoutCkeck from "../../../assets/images/soldout-check.png";
import MoneyPlant from "../../../assets/images/money-plant.png";
import AccountDetails from "../../../components/AccountDetails/AccountDetails";
import CustomModal from "../../../components/CustomModal/CustomModal";
import {
  DateFormatter,
  formatDateLong,
  dealsClosesIn,
  formatToLacCr,
  calculateSoldOutPercentage,
  calculateReturns,
  calculateDiscountAmount,
  calculateNetReturns,
  calculateAvailableAmountToInvest,
  calculatePlatformFee,
  calculateExpectedNetProfit,
  calculateExpectedNetRepayment,
} from "../../../helpers/helpers";
import dayjs from "dayjs";
import { GLOBAL_CONSTANTS } from "../../../constants/constants";
import Overview from "./Overview";
import InvestmentBreakdownRow from "../../../components/LiveDeals/InvestmentBreakdownRow";
import JvDealBuyButtons from "./JvDealBuyButtons";

const { Title, Text } = Typography;

const MIN_DEAL_AMOUNT = 200000; // 2L
const roundAmount = (num) => Math.round(Number(num || 0));

const BLOCK_TYPES = {
  TEXT: "DBT1000001",
  BULLET_LIST: "DBT1000002",
  CARD_GRID: "DBT1000003",
  PROGRESS_BAR: "DBT1000004",
  METRICS: "DBT1000005",
  STATUS: "DBT1000006",
  UPLOAD_DOCUMENTS: "DBT1000007",
};

const BULLET_COLOR_FALLBACK = "#101924";

const normalizeToken = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const resolveBulletColor = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return BULLET_COLOR_FALLBACK;
  if (/^#([\da-f]{3}|[\da-f]{6})$/i.test(normalized)) return normalized;

  // Allow CSS color names coming directly from API option values.
  return normalized;
};

const resolveBulletPrefix = (block = {}, index = 0) => {
  const rawPrefix =
    block?.prefixType || block?.bulletType || block?.prefix || "";
  const apiSymbol = block?.prefixSymbol || block?.bulletSymbol || "";
  const normalized = normalizeToken(rawPrefix);

  if (apiSymbol) return apiSymbol;
  if (normalized === "check") return "✓";
  if (normalized === "dot") return "•";
  if (normalized === "circle") return "○";
  if (normalized === "square") return "■";
  if (normalized === "number") return `${index + 1}.`;

  // Avoid showing raw type names like "check" in UI.
  return "•";
};

const getStatusTheme = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();
  if (normalizedStatus === "completed")
    return { color: "#16a34a", bg: "#dcfce7" };
  if (
    normalizedStatus === "in progress" ||
    normalizedStatus === "in_progress"
  ) {
    return { color: "#2563eb", bg: "#dbeafe" };
  }
  return { color: "#b45309", bg: "#ffedd5" };
};

const normalizeBlockType = (content = {}) => {
  const typeId = String(content?.blockTypeId || "").trim();
  const typeName = normalizeToken(
    content?.blockType || content?.blockTypeName || "",
  );

  if (typeId) return typeId;

  if (typeName === "text") return BLOCK_TYPES.TEXT;
  if (
    typeName === "bullet" ||
    typeName === "bullets" ||
    typeName === "bulletlist" ||
    typeName === "bulletlists"
  ) {
    return BLOCK_TYPES.BULLET_LIST;
  }
  if (typeName === "cardgrid" || typeName === "cardgrids")
    return BLOCK_TYPES.CARD_GRID;
  if (typeName === "progressbar" || typeName === "progressbars")
    return BLOCK_TYPES.PROGRESS_BAR;
  if (typeName === "metrics") return BLOCK_TYPES.METRICS;
  if (typeName === "status") return BLOCK_TYPES.STATUS;
  if (typeName === "uploaddocuments" || typeName === "uploaddocument") {
    return BLOCK_TYPES.UPLOAD_DOCUMENTS;
  }

  const knownIds = new Set(Object.values(BLOCK_TYPES));
  if (typeId && knownIds.has(typeId)) return typeId;

  return typeId || "";
};

const normalizeSectionBlocks = (blocks = []) =>
  (Array.isArray(blocks) ? blocks : []).map((block) => {
    if (block?.payload && typeof block.payload === "object") {
      return block.payload;
    }
    return block;
  });

const getSafeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parsePercentValue = (rawValue) => {
  if (rawValue === undefined || rawValue === null || rawValue === "")
    return null;
  const normalized = String(rawValue)
    .replace(/[%\s,]/g, "")
    .trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatSectionMetricValue = (block = {}) => {
  const prefix = block?.prefix || block?.prefixValue || "";
  const value = block?.value;
  const suffix = block?.suffix || block?.suffixValue || "";

  if (value === undefined || value === null || value === "") {
    return "Nil";
  }

  return `${prefix}${value}${suffix}`;
};

const DealSectionContent = ({ section }) => {
  const contents = (Array.isArray(section?.contents) ? section.contents : [])
    .slice()
    .sort(
      (a, b) => getSafeNumber(a?.contentOrder) - getSafeNumber(b?.contentOrder),
    );

  if (!contents.length) {
    return (
      <Typography style={{ color: "#40566D" }}>
        No content available for this section.
      </Typography>
    );
  }

  return (
    <div className="karny-evaluation-container">
      {contents.map((content) => {
        const blocks = normalizeSectionBlocks(content?.blocks);
        const blockType = normalizeBlockType(content);

        const renderTextBlocks = () => (
          <div>
            {blocks.map((block, index) => (
              <Typography
                key={`${content?.contentId || content?.title}-text-${index}`}
                style={{ color: "#101924", marginBottom: 8, fontSize: 14 }}
              >
                {block?.text || "-"}
              </Typography>
            ))}
          </div>
        );

        const renderBulletListBlocks = () => (
          <div>
            {blocks.map((block, index) => (
              <Flex
                key={`${content?.contentId || content?.title}-bullet-${index}`}
                align="flex-start"
                gap={8}
                style={{ marginBottom: 8 }}
              >
                <Typography
                  style={{
                    color: resolveBulletColor(
                      block?.bulletColor || content?.bulletColor,
                    ),
                    fontWeight: 600,
                  }}
                >
                  {resolveBulletPrefix(block, index)}
                </Typography>
                <Typography style={{ color: "#101924" }}>
                  {block?.item || block?.text || block?.value || "-"}
                </Typography>
              </Flex>
            ))}
          </div>
        );

        const renderCardGridBlocks = () => (
          <Row gutter={[14, 14]} style={{ padding: "8px 0" }}>
            {blocks
              .filter((block) => {
                const title =
                  block?.blockTitle || block?.title || block?.label || "";
                const value = block?.value || block?.metricValue || "";
                return (
                  String(title).trim().length > 0 ||
                  String(value).trim().length > 0
                );
              })
              .map((block, index) => {
                const iconTypeRaw =
                  block?.iconType ||
                  block?.prefixType ||
                  block?.bulletType ||
                  "dot";
                const iconType = normalizeToken(iconTypeRaw);
                const iconColor = resolveBulletColor(
                  block?.iconColorCode ||
                    block?.iconColor ||
                    block?.barColorCode ||
                    block?.barColor ||
                    block?.bulletColor,
                );
                const apiMarker =
                  block?.iconSymbol ||
                  block?.prefixSymbol ||
                  block?.bulletSymbol ||
                  "";
                const marker =
                  apiMarker || (iconType === "number" ? `${index + 1}.` : "•");
                const title =
                  block?.blockTitle || block?.title || block?.label || "-";
                const value = block?.value || block?.metricValue || "Nil";

                return (
                  <Col
                    xs={24}
                    md={12}
                    lg={8}
                    key={`${content?.contentId || content?.title}-card-${index}`}
                  >
                    <Card
                      size="small"
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        minHeight: 128,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                      bodyStyle={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "12px 16px",
                      }}
                    >
                      <div
                        style={{
                          color: iconColor,
                          fontSize: 34,
                          lineHeight: 1,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: 6,
                        }}
                      >
                        {iconType === "check" ? (
                          <CheckCircleOutlined />
                        ) : (
                          <span
                            style={{
                              fontSize: 28,
                              fontWeight: 700,
                              lineHeight: 1,
                            }}
                          >
                            {marker}
                          </span>
                        )}
                      </div>
                      <Typography
                        style={{
                          margin: 0,
                          fontSize: 18,
                          fontWeight: 700,
                          color: "#1f2937",
                          lineHeight: 1.2,
                        }}
                      >
                        {value}
                      </Typography>
                      <Typography
                        style={{
                          marginTop: 6,
                          marginBottom: 0,
                          fontSize: 15,
                          color: "#4b5563",
                          lineHeight: 1.2,
                        }}
                      >
                        {title}
                      </Typography>
                    </Card>
                  </Col>
                );
              })}
          </Row>
        );

        const renderProgressBarBlocks = () => (
          <div>
            {blocks.map((block, index) => {
              const percentage = Math.max(
                0,
                Math.min(100, getSafeNumber(block?.value)),
              );
              const barColor = resolveBulletColor(
                block?.barColorCode ||
                  block?.barColor ||
                  block?.iconColorCode ||
                  block?.iconColor ||
                  content?.bulletColor,
              );

              return (
                <div
                  key={`${content?.contentId || content?.title}-progress-${index}`}
                  style={{ marginBottom: 16 }}
                >
                  <Flex
                    align="center"
                    justify="space-between"
                    style={{ marginBottom: 6 }}
                  >
                    <Typography style={{ color: "#101924" }}>
                      {block?.label || "-"}
                    </Typography>
                    <Typography
                      style={{ color: "#40566D" }}
                    >{`${percentage}%`}</Typography>
                  </Flex>
                  <Progress
                    percent={percentage}
                    showInfo={false}
                    strokeColor={barColor}
                  />
                </div>
              );
            })}
          </div>
        );

        const renderMetricsBlocks = () => (
          <Row gutter={[14, 14]}>
            {blocks
              .filter((block) => {
                const title =
                  block?.metricTitle || block?.title || block?.label || "";
                const value = block?.value || block?.metricValue || "";
                return (
                  String(title).trim().length > 0 ||
                  String(value).trim().length > 0
                );
              })
              .map((block, index) => (
                <Col
                  xs={24}
                  md={12}
                  lg={8}
                  key={`${content?.contentId || content?.title}-${block?.metricTitle || index}`}
                >
                  <Card
                    size="small"
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 5,
                    }}
                    bodyStyle={{ padding: 14 }}
                  >
                    <Typography
                      style={{
                        display: "block",
                        marginBottom: 8,
                        fontSize: 14,
                        color: "#6b7280",
                      }}
                    >
                      {block?.metricTitle ||
                        block?.title ||
                        block?.label ||
                        "Metric"}
                    </Typography>
                    <Typography
                      style={{
                        margin: 0,
                        fontSize: 21,
                        color: "#16a34a",
                        fontWeight: 700,
                      }}
                    >
                      {formatSectionMetricValue(block)}
                    </Typography>
                  </Card>
                </Col>
              ))}
          </Row>
        );

        const renderStatusBlocks = () => (
          <Row gutter={[12, 12]} style={{ padding: "10px 0" }}>
            {blocks
              .filter((block) => {
                const title =
                  block?.statusTitle || block?.title || block?.label || "";
                const description = block?.description || block?.desc || "";
                return (
                  String(title).trim().length > 0 ||
                  String(description).trim().length > 0
                );
              })
              .map((block, index) => {
                const statusValue =
                  block?.status ||
                  block?.statusLabel ||
                  block?.statusName ||
                  "Pending";
                const theme = getStatusTheme(statusValue);

                return (
                  <Col
                    xs={24}
                    md={12}
                    lg={8}
                    key={`${content?.contentId || content?.title}-status-${index}`}
                  >
                    <Card
                      size="small"
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 5,
                        background: "#fff",
                      }}
                      bodyStyle={{ padding: "10px 12px" }}
                    >
                      <Flex
                        align="center"
                        justify="space-between"
                        gap={8}
                        style={{ marginBottom: 8 }}
                      >
                        <Typography
                          style={{
                            fontSize: 14,
                            fontWeight: 400,
                            color: "#6b7280",
                            lineHeight: 1.2,
                          }}
                        >
                          {block?.statusTitle ||
                            block?.title ||
                            block?.label ||
                            "Status"}
                        </Typography>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 999,
                            padding: "2px 10px",
                            fontSize: 10,
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            color: theme.color,
                            background: theme.bg,
                          }}
                        >
                          {statusValue}
                        </span>
                      </Flex>
                      <Typography
                        style={{
                          margin: 0,
                          fontSize: 12,
                          fontWeight: 500,
                          color: "#111827",
                          lineHeight: 1.2,
                        }}
                      >
                        {block?.description || block?.desc || "-"}
                      </Typography>
                    </Card>
                  </Col>
                );
              })}
          </Row>
        );

        const renderUploadDocumentsBlocks = () => (
          <div
            style={{
              padding: "8px 0",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {blocks
              .filter((block) => {
                const fileName = block?.fileName || "";
                const fileUrl = block?.fileUrl || "";
                return (
                  String(fileName).trim().length > 0 ||
                  String(fileUrl).trim().length > 0
                );
              })
              .map((block, index) => (
                <ViewOnlyDocumentBlock
                  key={`${content?.contentId || content?.title}-upload-${index}`}
                  fileName={block?.fileName || "Document"}
                  fileUrl={block?.fileUrl || ""}
                />
              ))}
          </div>
        );

        const renderBlocksByType = () => {
          if (!blocks.length) {
            return (
              <Typography style={{ color: "#40566D" }}>
                No data available.
              </Typography>
            );
          }

          if (blockType === BLOCK_TYPES.TEXT) return renderTextBlocks();
          if (blockType === BLOCK_TYPES.BULLET_LIST)
            return renderBulletListBlocks();
          if (blockType === BLOCK_TYPES.CARD_GRID)
            return renderCardGridBlocks();
          if (blockType === BLOCK_TYPES.PROGRESS_BAR)
            return renderProgressBarBlocks();
          if (blockType === BLOCK_TYPES.METRICS) return renderMetricsBlocks();
          if (blockType === BLOCK_TYPES.STATUS) return renderStatusBlocks();
          if (blockType === BLOCK_TYPES.UPLOAD_DOCUMENTS)
            return renderUploadDocumentsBlocks();

          return (
            <Typography style={{ color: "#40566D" }}>
              No data available.
            </Typography>
          );
        };

        return (
          <Card
            key={content?.contentId || content?.title}
            style={{ marginBottom: 16 }}
          >
            {content?.title ? (
              <Title level={5} style={{ marginBottom: 10, fontSize: 14 }}>
                {content.title}
              </Title>
            ) : null}

            {renderBlocksByType()}
          </Card>
        );
      })}
    </div>
  );
};

const DealDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    dealId,
    enteredDealAmount,
    expectedDealCategoryId,
    expectedDealCategoryName,
    options,
  } = location.state || {};
  const {
    deal,
    loading,
    dealSections = [],
    dealSectionsLoading,
  } = useSelector((state) => state.deal);
  const { user } = useSelector((state) => state.auth);
  const { accBalance } = useSelector((state) => state.manageFunds);
  const [yourDealError, setYourDealError] = useState("");

  const isJointVenturesDeal = deal?.dealCategoryId === "DCT1000002";
  const isInvoiceFinancingDeal = deal?.dealCategoryId === "DCT1000001";
  const isSupplyChainDeal = deal?.dealCategoryId === "DCT1000006";
  const isInvoiceStyleDeal = isInvoiceFinancingDeal || isSupplyChainDeal;

  const apiMinDealAmount = Number(
    (deal?.minDealAmt ??
      deal?.minInvestmentCriteria ??
      deal?.minimumDealAmount) ||
      0,
  );
  const validApiMinDealAmount =
    Number.isFinite(apiMinDealAmount) && apiMinDealAmount > 0
      ? apiMinDealAmount
      : MIN_DEAL_AMOUNT;
  const minDealAmountForThisDeal = isJointVenturesDeal
    ? validApiMinDealAmount
    : MIN_DEAL_AMOUNT;

  const [yourDealAmount, setYourDealAmmount] = useState(0);

  // "Amount to invest now" range
  const [amountToInvestNow, setAmountToInvestNow] = useState(0);
  const [minInvestNow, setMinInvestNow] = useState(0);
  const [maxInvestNow, setMaxInvestNow] = useState(0);
  const [isAmountDisabled, setIsAmountDisabled] = useState(false);

  const [amountError, setAmountError] = useState("");
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [addBalanceOpen, setAddBalanceOpen] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState();
  const [intrestModal, setIntrestModal] = useState({
    open: false,
    option: "",
  });

  const isBannerPresent = user?.userStatusId !== "USM1000001";
  const topOffset = isBannerPresent ? 150 : 84;
  const jvDealName = deal?.dealName || "";
  const descriptionText = isJointVenturesDeal
    ? deal?.dealDescription || ""
    : deal?.entDescription ||
      deal?.businessDescription ||
      deal?.dealDescription ||
      "";

  // fetch deal
  useEffect(() => {
    dispatch(getDealById(dealId));
  }, [dispatch, dealId]);

  useEffect(() => {
    const fetchedCategoryId = String(deal?.dealCategoryId || "").trim();
    const selectedCategoryId = String(expectedDealCategoryId || "").trim();
    if (
      !fetchedCategoryId ||
      (selectedCategoryId && fetchedCategoryId !== selectedCategoryId)
    ) {
      return;
    }
    dispatch(getDealSections(dealId));
  }, [dispatch, dealId, deal?.dealCategoryId, expectedDealCategoryId]);

  // derive main investment constraints when deal loads
  useEffect(() => {
    if (isInvoiceStyleDeal && (!deal?.annualDiscountRate || !deal?.tenure))
      return;

    const availableAmountToInvest = Number(
      calculateAvailableAmountToInvest(
        deal?.approvedFundAmt || 0,
        deal?.fundedAmt || 0,
      ),
    );

    let minDealAmt;
    let maxDealAmt;
    let initialDealAmt;
    let disableInput;

    if (isInvoiceStyleDeal) {
      minDealAmt = availableAmountToInvest;
      maxDealAmt = availableAmountToInvest;
      initialDealAmt = availableAmountToInvest;
      disableInput = true;
    } else {
      const maxNowByAvailable = availableAmountToInvest;
      const isRemainingBelowMinInvestNow =
        maxNowByAvailable < minDealAmountForThisDeal;

      if (!isRemainingBelowMinInvestNow) {
        // RULE 1: available meets minimum Amount to Invest Now policy
        minDealAmt = minDealAmountForThisDeal;
        maxDealAmt = availableAmountToInvest;
        disableInput = false;

        const fromCard =
          enteredDealAmount !== undefined && enteredDealAmount !== null
            ? Number(enteredDealAmount)
            : maxDealAmt;
        initialDealAmt = Math.min(Math.max(fromCard, minDealAmt), maxDealAmt);
      } else {
        // RULE 2: final remaining amount is below minimum -> allow full remaining amount
        minDealAmt = maxDealAmt = availableAmountToInvest;
        initialDealAmt = availableAmountToInvest;
        disableInput = true;
      }
    }

    setIsAmountDisabled(disableInput);
    setYourDealAmmount(initialDealAmt);

    // map deal range → invest-now range
    setMinInvestNow(minDealAmt);
    setMaxInvestNow(maxDealAmt);

    setAmountToInvestNow(initialDealAmt);
    setAmountError("");
    validateYourDealAmount(initialDealAmt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal, enteredDealAmount, minDealAmountForThisDeal]);

  const validateYourDealAmount = (dealAmt) => {
    const available = Number(availableAmountToInvest || 0);
    const minRequired = minDealAmountForThisDeal;

    if (Number.isNaN(dealAmt)) return;
    if (available >= minRequired && Math.round(dealAmt) < minRequired) {
      setYourDealError(
        `Your Deal Amount cannot be less than ₹ ${minRequired.toLocaleString("en-IN")}`,
      );
    } else if (Math.round(dealAmt) > available) {
      setYourDealError(
        `Your Deal Amount cannot be more than ₹ ${available.toLocaleString("en-IN")}`,
      );
    } else {
      setYourDealError("");
    }
  };

  // derived values
  const availableAmountToInvest = Number(
    calculateAvailableAmountToInvest(
      deal?.approvedFundAmt || 0,
      deal?.fundedAmt || 0,
    ),
  );
  const displayedAvailableAmount = availableAmountToInvest;

  const minAmount = roundAmount(minInvestNow);
  const maxAmount = roundAmount(maxInvestNow);

  const isSliderDisabled = isAmountDisabled || maxAmount <= minAmount;

  const discountAmount = Number(
    calculateDiscountAmount(
      yourDealAmount,
      deal?.annualDiscountRate,
      deal?.tenure,
    ) || 0,
  );

  const soldOutPercentage = calculateSoldOutPercentage(
    deal?.fundedAmt,
    deal?.approvedFundAmt,
  );
  const returns = calculateReturns(deal?.annualDiscountRate);
  const resolvedRoiValue = (() => {
    const rawRoi = deal?.jvDealExpectedROI ?? deal?.expectedROI;
    const parsedRoi = parsePercentValue(rawRoi);
    if (!Number.isFinite(parsedRoi)) return returns;
    const normalizedRoi = parsedRoi <= 1 ? parsedRoi * 100 : parsedRoi;
    return Number(normalizedRoi.toFixed(2));
  })();
  const resolvedProfitShareValue = (() => {
    const rawProfitShare =
      deal?.profitShare ??
      deal?.jvDealProfitShare ??
      deal?.jvDealProfitSharePercentage;
    const parsedProfitShare = parsePercentValue(rawProfitShare);
    if (!Number.isFinite(parsedProfitShare)) return null;
    const normalizedProfitShare =
      parsedProfitShare <= 1 ? parsedProfitShare * 100 : parsedProfitShare;
    return Number(normalizedProfitShare.toFixed(2));
  })();
  const netReturns = calculateNetReturns(
    discountAmount,
    deal?.platformFee,
    deal?.penalCharge,
  );
  const jvRoiAmount = isJointVenturesDeal
    ? (Number(amountToInvestNow || 0) * Number(resolvedRoiValue || 0)) / 100
    : 0;
  const jvKarncyCarryAmount = isJointVenturesDeal
    ? (jvRoiAmount * Number(GLOBAL_CONSTANTS.JV_KARNCY_CARRY_PERCENT || 0)) /
      100
    : 0;
  const expectedGrossProfit = isJointVenturesDeal
    ? jvRoiAmount
    : discountAmount;
  const platformFee = isJointVenturesDeal
    ? jvKarncyCarryAmount
    : calculatePlatformFee(yourDealAmount, deal?.platformFee, deal?.tenure);
  const expectedNetProfit = isJointVenturesDeal
    ? expectedGrossProfit - platformFee
    : calculateExpectedNetProfit(discountAmount, platformFee);
  const expectedNetRepayment = isJointVenturesDeal
    ? Number(amountToInvestNow || 0) + Number(expectedGrossProfit || 0)
    : calculateExpectedNetRepayment(
        amountToInvestNow,
        discountAmount,
        platformFee,
      );
  const netReturnsForOrderSummary = isJointVenturesDeal
    ? expectedGrossProfit
    : netReturns;
  const primaryRateLabel = isJointVenturesDeal
    ? "Expected ROI"
    : "Interest Rate";
  const primaryRateValue = isJointVenturesDeal ? resolvedRoiValue : returns;
  const investmentBreakdownRows = [
    {
      label: "Expected Margin",
      tooltip: "Gross profit is the profit before platform fee and taxes.",
      value: expectedGrossProfit,
    },
    ...(!isJointVenturesDeal
      ? [
          {
            label: "Platform Fee",
            tooltip: "Platform fee is charged for the service provided.",
            value: platformFee,
          },
          {
            label: "Expected Net Margin",
            tooltip:
              "Net profit is the amount after deducting platform fee and other charges.",
            value: expectedNetProfit,
          },
        ]
      : []),
    {
      label: "Total Payout",
      tooltip: isJointVenturesDeal
        ? "Total payout is the sum of amount to invest now and expected margin."
        : "Net repayment is the total amount that will be returned after maturity.",
      value: expectedNetRepayment,
    },
  ];

  const isBuyNowDisabled =
    Number(yourDealAmount) > Number(accBalance?.amount) ||
    amountToInvestNow <= 0 ||
    !!yourDealError ||
    !!amountError;

  const handleAmountToInvestChange = (value) => {
    // NumericInput + parser guarantees digits only, but value can still be "" or null
    if (value === null || value === undefined || value === "") {
      setAmountToInvestNow(0);
      setYourDealAmmount(0);
      setAmountError("");
      return;
    }

    const num = Number(value);

    if (Number.isNaN(num)) {
      setAmountError("Please enter a valid amount.");
      return;
    }

    const roundedMin = Math.round(minAmount);
    const roundedMax = Math.round(maxAmount);

    let error = "";
    if (Math.round(num) < roundedMin) {
      error = `Amount cannot be less than ₹ ${roundedMin.toLocaleString(
        "en-IN",
      )}`;
    } else if (Math.round(num) > roundedMax) {
      error = `Amount cannot be more than ₹ ${roundedMax.toLocaleString(
        "en-IN",
      )}`;
    }

    setAmountError(error);
    setAmountToInvestNow(num);

    setYourDealAmmount(num);
    validateYourDealAmount(num);
  };

  const handleSliderChange = (value) => {
    const num = Number(value) || 0;
    setAmountError(""); // slider always within [min,max]
    setAmountToInvestNow(num);

    setYourDealAmmount(num);
    validateYourDealAmount(num);
  };

  const clampedSliderValue = Math.min(
    Math.max(amountToInvestNow || 0, Number(minAmount)),
    Number(maxAmount),
  );

  const handleBuyNow = () => {
    const data = {
      dealId: deal?.dealId,
      entName: deal?.entName || deal?.dealName || deal?.businessName,
      netReturns: netReturnsForOrderSummary,
      dealValue: deal?.approvedFundAmt,
      repaymentOn: dayjs(deal?.dealExpiryDate)
        .add(deal?.tenure, "day")
        .format("YYYY-MM-DD"),
      maturity: deal?.tenure,
      expectedGrossProfit: expectedGrossProfit,
      platformFee: platformFee,
      expectedNetYield: isJointVenturesDeal
        ? expectedGrossProfit
        : expectedNetProfit,
      expectedNetRepayment: expectedNetRepayment,
      amountToPayNow: amountToInvestNow,
      dealAmt: yourDealAmount.toString(),
      orgId: deal.orgId || deal.dealOrgId,
      busEntId: deal.busEntId,
      isJointVenturesDeal,
    };
    navigate("/order-summary", { state: { dealInfo: data } });
  };

  const toggleDescription = () => {
    setIsDescriptionExpanded((prev) => !prev);
  };

  const dealCategoryId = String(deal?.dealCategoryId || "").trim();
  const dealCategoryName = deal?.dealCategoryName || "";
  const selectedDealCategoryId = String(expectedDealCategoryId || "").trim();
  const isCategoryMatched =
    !selectedDealCategoryId ||
    !dealCategoryId ||
    selectedDealCategoryId === dealCategoryId;

  const dynamicTabItems = useMemo(
    () =>
      (dealSections || [])
        .slice()
        .sort(
          (a, b) =>
            getSafeNumber(a?.sectionOrder) - getSafeNumber(b?.sectionOrder),
        )
        .map((section) => ({
          key: String(section?.sectionId),
          label: section?.sectionName || "Section",
          children: <DealSectionContent section={section} />,
        })),
    [dealSections],
  );

  const legacyTabItems = useMemo(
    () => [
      {
        key: "1",
        label: "Overview",
        children: <Overview deal={deal} />,
      },
      {
        key: "2",
        label: "Enterprise",
        children: <Enterprise deal={deal} />,
      },
      {
        key: "3",
        label: "Business",
        children: <Business deal={deal} />,
      },
      {
        key: "4",
        label: "Karncy Evaluation",
        children: <KarncyEvaluation deal={deal} />,
      },
    ],
    [deal],
  );

  const tabItems = useMemo(() => {
    if (!isCategoryMatched) {
      return [];
    }
    return dynamicTabItems.length > 0 ? dynamicTabItems : legacyTabItems;
  }, [isCategoryMatched, dynamicTabItems, legacyTabItems]);

  useEffect(() => {
    if (!tabItems.length) {
      setActiveTabKey(undefined);
      return;
    }
    setActiveTabKey((current) => {
      if (tabItems.some((item) => item.key === current)) {
        return current;
      }
      return tabItems[0].key;
    });
  }, [tabItems]);

  return (
    <>
      {loading || (Boolean(dealCategoryId) && dealSectionsLoading) ? (
        <Loader />
      ) : (
        <>
          <CustomButton
            variant="text"
            color="primary"
            icon={<ArrowLeftOutlined />}
            iconPosition="start"
            onClick={() => navigate(-1)}
            style={{ color: "#40566D", fontSize: 14, padding: 5 }}
          >
            Deal ID: {dealId}
          </CustomButton>
          <Row>
            <Col xs={24} lg={16}>
              <div className="deal-card">
                <Flex
                  align="center"
                  justify="space-between"
                  style={{ marginBottom: 15 }}
                >
                  {isJointVenturesDeal ? (
                    <div>
                      <Title level={5} style={{ margin: 0 }}>
                        {jvDealName}
                      </Title>
                    </div>
                  ) : deal?.entLogo ? (
                    <Image
                      src={deal?.entLogo}
                      height={50}
                      width={50}
                      preview={false}
                      alt="Enterprise Logo"
                      style={{
                        objectFit: "contain",
                        borderRadius: 10,
                      }}
                    />
                  ) : (
                    <div>
                      <Title level={5} style={{ margin: 0 }}>
                        {deal?.entName ||
                          deal?.dealName ||
                          deal?.businessName ||
                          "Deal Details"}
                      </Title>
                    </div>
                  )}
                  <Flex vertical align="flex-end" gap={8}>
                    <div className="sold-out-tag">{`${soldOutPercentage}% Sold Out`}</div>
                    {dealCategoryName ? (
                      <Typography
                        style={{
                          fontSize: 12,
                          color: "#40566D",
                          fontWeight: 600,
                        }}
                        className="deal-chip"
                      >
                        {dealCategoryName}
                      </Typography>
                    ) : null}
                  </Flex>
                </Flex>
                {!isJointVenturesDeal && deal?.entLogo ? (
                  <Title level={5}>
                    {deal?.entName ||
                      deal?.dealName ||
                      deal?.businessName ||
                      ""}
                  </Title>
                ) : null}
                <Typography style={{ marginBottom: 15, fontSize: 14 }}>
                  {isDescriptionExpanded
                    ? descriptionText
                    : descriptionText.length > 100
                      ? `${descriptionText.slice(0, 100)}...`
                      : descriptionText}
                  {descriptionText.length > 100 ? (
                    <CustomButton
                      type="link"
                      style={{ padding: 0, marginLeft: 5, fontSize: 12 }}
                      onClick={toggleDescription}
                    >
                      {isDescriptionExpanded ? "Read Less" : "Read More"}
                    </CustomButton>
                  ) : null}
                </Typography>
                <Flex gap="middle" style={{ marginBottom: 25 }}>
                  {deal?.dealTypeName === "Karncy Assured Invoice" && (
                    <div className="deal-tags">Karncy Assured</div>
                  )}
                </Flex>
                <div className="deal-constraint-container">
                  <div className="deal-constraint">
                    <Image
                      src={ReturnsImage}
                      preview={false}
                      alt="Interest Rate"
                    />
                    <div className="deal-constraint-details">
                      <Typography style={{ fontSize: 13 }}>
                        {primaryRateLabel}
                      </Typography>
                      <Title level={5}>{primaryRateValue}%</Title>
                    </div>
                  </div>
                  {isJointVenturesDeal ? (
                    <div className="deal-constraint">
                      <Image
                        src={ReturnsImage}
                        preview={false}
                        alt="Profit Share"
                      />
                      <div className="deal-constraint-details">
                        <Typography style={{ fontSize: 13 }}>
                          Profit Share
                        </Typography>
                        <Title level={5}>
                          {resolvedProfitShareValue !== null
                            ? `${resolvedProfitShareValue}%`
                            : "N/A"}
                        </Title>
                      </div>
                    </div>
                  ) : null}
                  {!isJointVenturesDeal ? (
                    <div className="deal-constraint">
                      <Image
                        src={MaturityImage}
                        preview={false}
                        alt="Maturity"
                      />
                      <div className="deal-constraint-details">
                        <Typography style={{ fontSize: 13 }}>
                          Maturity
                        </Typography>
                        <Title level={5}>{deal?.tenure || "N/A"} days</Title>
                      </div>
                    </div>
                  ) : null}
                  <div className="deal-constraint">
                    <Image
                      src={DealValueImage}
                      preview={false}
                      alt="Deal Value"
                    />
                    <div className="deal-constraint-details">
                      <Typography style={{ fontSize: 13 }}>
                        Deal Value
                      </Typography>
                      <Title level={5}>
                        ₹ {formatToLacCr(Number(deal?.approvedFundAmt))}
                      </Title>
                    </div>
                  </div>
                </div>
              </div>
              <div className="deal-details-tabs-container-web">
                {!isCategoryMatched ? (
                  <Typography style={{ color: "#40566D" }}>
                    Selected deal category does not match loaded deal category.
                    {selectedDealCategoryId
                      ? ` Expected: ${expectedDealCategoryName || selectedDealCategoryId}.`
                      : ""}
                  </Typography>
                ) : tabItems.length ? (
                  <Tabs
                    activeKey={activeTabKey}
                    onChange={setActiveTabKey}
                    items={tabItems}
                  />
                ) : (
                  <Typography style={{ color: "#40566D" }}>
                    No tab data available for this deal.
                  </Typography>
                )}
              </div>
            </Col>
            <Col
              xs={24}
              lg={8}
              className="sticky-column"
              style={{ top: `${topOffset}px` }}
            >
              {(deal?.statusName === "approved" ||
                deal?.statusName === "funded") && (
                <Card className="deal-details">
                  {!isJointVenturesDeal ? (
                    <Flex
                      align="center"
                      justify="space-between"
                      style={{ marginBottom: 30 }}
                    >
                      <Typography style={{ color: "#40566D" }}>
                        Available Amount to Invest
                      </Typography>
                      <Typography style={{ color: "#40566D", fontWeight: 600 }}>
                        ₹ {formatToLacCr(Number(displayedAvailableAmount))}
                      </Typography>
                    </Flex>
                  ) : null}

                  {isInvoiceStyleDeal && (
                    <Alert
                      type="warning"
                      showIcon
                      message="Cannot customize amount. You need to fund the deal in full."
                      style={{
                        marginBottom: 16,
                        borderRadius: 8,
                        fontSize: 13,
                      }}
                    />
                  )}

                  {/* Amount to Invest Now */}
                  {isJointVenturesDeal && (
                    <Flex align="center" justify="space-between">
                      <Typography
                        style={{
                          color: "#40566D",
                          fontSize: 14,
                          fontWeight: 600,
                        }}
                      >
                        Amount to Invest Now
                      </Typography>

                      <InputNumber
                        changeOnWheel={false}
                        value={
                          amountToInvestNow === ""
                            ? undefined
                            : amountToInvestNow
                        }
                        precision={0}
                        inputMode="numeric"
                        disabled={
                          isAmountDisabled ||
                          isInvoiceStyleDeal ||
                          isSliderDisabled
                        }
                        // NOTE: no min / max props here (we validate manually)
                        formatter={(v) =>
                          // show blank when user cleared (v may be undefined)
                          v === undefined || v === ""
                            ? ""
                            : new Intl.NumberFormat("en-IN", {
                                minimumFractionDigits: 0,
                              }).format(Number(v))
                        }
                        parser={(v) => (v || "").replace(/[^\d]/g, "")} // keep only digits
                        onChange={(val) =>
                          handleAmountToInvestChange(val === null ? "" : val)
                        }
                        onKeyDown={(e) => {
                          // allow navigation & ctrl/meta keys
                          const allowed = [
                            "Backspace",
                            "Delete",
                            "Tab",
                            "ArrowLeft",
                            "ArrowRight",
                            "Home",
                            "End",
                          ];
                          if (allowed.includes(e.key) || e.ctrlKey || e.metaKey)
                            return;
                          // block non-digit characters
                          if (!/^\d$/.test(e.key)) e.preventDefault();
                        }}
                        onPaste={(e) => {
                          const text = e.clipboardData.getData("text");
                          if (!/^\d+$/.test(text)) e.preventDefault();
                        }}
                        size="large"
                        prefix="₹"
                        variant="borderless"
                        className={`number-input ${amountToInvestNow < minAmount ? "number-input-error" : ""}`}
                      />
                    </Flex>
                  )}

                  {amountError && (
                    <Typography
                      style={{
                        fontSize: 12,
                        color: "#750A0A",
                        marginTop: 5,
                      }}
                    >
                      {amountError}
                    </Typography>
                  )}

                  {Number(amountToInvestNow) > Number(accBalance?.amount) && (
                    <Flex justify="flex-end" style={{ marginTop: 10 }}>
                      <Typography style={{ fontSize: 12, color: "#750A0A" }}>
                        Insufficient balance in wallet.
                        <Text
                          onClick={() => setAddBalanceOpen(true)}
                          style={{
                            cursor: "pointer",
                            fontSize: 12,
                            color: "#750A0A",
                          }}
                          strong
                        >
                          {" "}
                          Add Money
                        </Text>
                      </Typography>
                      <CustomModal
                        open={addBalanceOpen}
                        centered
                        onCancel={() => setAddBalanceOpen(false)}
                        content={<AccountDetails />}
                      />
                    </Flex>
                  )}

                  {isJointVenturesDeal && (
                    <Slider
                      min={Number(minAmount)}
                      max={Number(maxAmount)}
                      value={clampedSliderValue}
                      onChange={handleSliderChange}
                      step={isSliderDisabled ? 1 : 5000}
                      disabled={isSliderDisabled || isInvoiceStyleDeal}
                      style={{ marginBottom: 30, marginTop: 30 }}
                    />
                  )}

                  <Flex vertical gap="large" style={{ marginBottom: 20 }}>
                    {investmentBreakdownRows.map((row) => (
                      <InvestmentBreakdownRow key={row.label} {...row} />
                    ))}
                    {yourDealError && (
                      <Typography
                        style={{
                          fontSize: 12,
                          color: "#750A0A",
                          marginBottom: 6,
                        }}
                      >
                        {yourDealError}
                      </Typography>
                    )}
                  </Flex>

                  {isJointVenturesDeal ? (
                    <JvDealBuyButtons
                      options={options}
                      dealId={dealId}
                      intrestModal={intrestModal}
                      setIntrestModal={setIntrestModal}
                    />
                  ) : (
                    <CustomButton
                      type="primary"
                      disabled={isBuyNowDisabled}
                      onClick={handleBuyNow}
                      style={{ backgroundColor: "#00874F", marginBottom: 10 }}
                      block
                    >
                      Buy Now
                    </CustomButton>
                  )}

                  {isInvoiceStyleDeal ? (
                    <Flex gap="small" align="center" justify="center">
                      <ClockCircleOutlined
                        style={{ color: "#40566D", fontSize: 12 }}
                      />
                      <Typography style={{ color: "#40566D", fontSize: 12 }}>
                        Closes in {dealsClosesIn(deal?.dealExpiryDate)}
                      </Typography>
                    </Flex>
                  ) : null}
                </Card>
              )}

              {(deal?.statusName === "agreement" ||
                deal?.statusName === "disbursed") && (
                <div className="soldout-container">
                  <Image preview={false} src={SoldoutCkeck} alt="Check logo" />
                  <Typography
                    style={{ color: "#101924", fontWeight: 600, fontSize: 20 }}
                  >
                    {`${deal?.invCount} investor${
                      deal?.invCount > 1 ? "s" : ""
                    } have invested in this deal`}
                  </Typography>
                  <Typography
                    style={{ color: "#40566D", fontSize: 16, fontWeight: 600 }}
                  >
                    Sold out on{" "}
                    {deal?.soldOutAt
                      ? DateFormatter(deal.soldOutAt)
                      : formatDateLong(deal?.fundedDate)}
                  </Typography>
                </div>
              )}

              {deal?.statusName === "settled" && (
                <div className="soldout-container">
                  <Image preview={false} src={MoneyPlant} alt="Check logo" />
                  <Typography style={{ color: "#40566D" }}>
                    {`${deal?.invCount} investor${
                      deal?.invCount > 1 ? "s" : ""
                    } received`}
                  </Typography>
                  <Typography
                    style={{ color: "#101924", fontWeight: 600, fontSize: 20 }}
                  >
                    {`total returns of ${formatToLacCr(deal?.investorPayout)}`}
                  </Typography>
                </div>
              )}
            </Col>
          </Row>

          <div className="deal-details-tabs-container-mobile">
            <Tabs
              activeKey={activeTabKey}
              onChange={setActiveTabKey}
              items={tabItems}
              style={{ width: "100%" }}
            />
          </div>
        </>
      )}
    </>
  );
};

export default DealDetails;
